import { useCallback, useMemo, useState } from "react"
import { useRouter } from "next/compat/router"

type FilterScalar = string | number | boolean | null | undefined
type FilterOption =
  | FilterScalar
  | {
      value: FilterScalar
      label: string
      aliases?: readonly FilterScalar[]
    }
type CollectionFilterField<T> = {
  id: string
  label: string
  value: (row: T) => FilterScalar | FilterScalar[]
  formatValue?: (value: string) => string
} & (
  | {
      kind: "select"
      options: readonly FilterOption[]
      /** Keeps `options` in the given order instead of sorting by label. */
      preserveOptionOrder?: boolean
      /** Renders the options as a checkmark menu that keeps one selection. */
      single?: boolean
      /**
       * Set while the data behind `value` is loading or failed to load. The
       * field's selection is then neither applied nor shown as active, and its
       * menu shows `message` (with a retry on error) instead of options.
       */
      unavailable?: CollectionFilterUnavailable
    }
  | { kind: "text" | "number" | "date"; options?: never }
)
type CollectionFilterUnavailable =
  | { state: "loading"; message: string }
  | { state: "error"; message: string; onRetry: () => void }
export interface CollectionFilterValue {
  text?: string
  selected?: string[]
  min?: string
  max?: string
}
export interface CollectionFilterControls {
  isLoading?: boolean
  properties: {
    id: string
    label: string
    kind: CollectionFilterField<unknown>["kind"]
    options: { id: string; label: string }[]
    single?: boolean
    unavailable?: CollectionFilterUnavailable
  }[]
  values: Record<string, CollectionFilterValue>
  onChange: (id: string, value: CollectionFilterValue) => void
  reset: () => void
  activeCount: number
}
const EMPTY_VALUES: Record<string, CollectionFilterValue> = {}
export function isCollectionFilterActive(value: CollectionFilterValue) {
  return Boolean(
    value.text?.trim() || value.selected?.length || value.min || value.max,
  )
}
function scalarValues<T>(field: CollectionFilterField<T>, row: T) {
  const value = field.value(row)
  const values = Array.isArray(value) ? value : [value]
  return values.length ? values : [null]
}
function dateBound(value: string, end: boolean) {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
  const date = new Date(dateOnly ? `${value}T00:00:00` : value)
  if (end && dateOnly) {
    date.setHours(23, 59, 59, 999)
  }
  return date.getTime()
}
export function useCollectionFilterState(scopeKey = "") {
  const router = useRouter()
  const scope = JSON.stringify([router?.asPath.split("?")[0] ?? "", scopeKey])
  const [state, setState] = useState({ scope, values: EMPTY_VALUES })
  const values = state.scope === scope ? state.values : EMPTY_VALUES
  const onChange = useCallback(
    (id: string, value: CollectionFilterValue) => {
      setState((previous) => ({
        scope,
        values: {
          ...(previous.scope === scope ? previous.values : EMPTY_VALUES),
          [id]: value,
        },
      }))
    },
    [scope],
  )
  const reset = useCallback(
    () => setState({ scope, values: EMPTY_VALUES }),
    [scope],
  )
  return {
    values,
    onChange,
    reset,
    activeCount: Object.values(values).filter(isCollectionFilterActive).length,
  }
}
/** Filters a fetched collection before sorting, grouping, and keyboard navigation. */
export function useCollectionFilters<T>(
  rows: T[],
  fields: CollectionFilterField<T>[],
  scopeKey = "",
  controlledState?: ReturnType<typeof useCollectionFilterState>,
) {
  const localState = useCollectionFilterState(scopeKey)
  const {
    values: storedValues,
    onChange,
    reset,
  } = controlledState ?? localState
  const values = useMemo(() => {
    const unavailableIds = new Set(
      fields
        .filter((field) => field.kind === "select" && field.unavailable)
        .map((field) => field.id),
    )
    if (unavailableIds.size === 0) {
      return storedValues
    }
    return Object.fromEntries(
      Object.entries(storedValues).filter(([id]) => !unavailableIds.has(id)),
    )
  }, [fields, storedValues])
  const properties = useMemo(
    () =>
      fields.map((field) => {
        const options = new Map<string, string>()
        if (field.kind === "select") {
          field.options.forEach((choice) => {
            const option =
              typeof choice === "object" && choice !== null ? choice : null
            const value =
              typeof choice === "object" && choice !== null
                ? choice.value
                : choice
            const scalar = value == null || value === "" ? null : String(value)
            options.set(
              JSON.stringify(scalar),
              option?.label ??
                (scalar === null
                  ? "Not Set"
                  : (field.formatValue?.(scalar) ?? scalar)),
            )
          })
          for (const id of values[field.id]?.selected ?? []) {
            if (!options.has(id)) {
              const parsed: unknown = JSON.parse(id)
              const scalar = typeof parsed === "string" ? parsed : null
              options.set(
                id,
                scalar === null
                  ? "Not Set"
                  : (field.formatValue?.(scalar) ?? scalar),
              )
            }
          }
        }
        const list = Array.from(options, ([id, label]) => ({ id, label }))
        return {
          ...field,
          options:
            field.kind === "select" && field.preserveOptionOrder
              ? list
              : list.sort((a, b) => a.label.localeCompare(b.label)),
        }
      }),
    [fields, values],
  )
  const filteredRows = useMemo(() => {
    return !fields.some((field) =>
      isCollectionFilterActive(values[field.id] ?? {}),
    )
      ? rows
      : rows.filter((row) =>
          fields.every((field) => {
            const filter = values[field.id]
            if (!filter || !isCollectionFilterActive(filter)) {
              return true
            }
            return scalarValues(field, row).some((value) => {
              if (field.kind === "select") {
                const selected = filter.selected
                if (!selected?.length) {
                  return true
                }
                const encoded = JSON.stringify(
                  value == null || value === "" ? null : String(value),
                )
                if (selected.includes(encoded)) {
                  return true
                }
                return (
                  field.options?.some(
                    (option) =>
                      typeof option === "object" &&
                      option !== null &&
                      selected.includes(
                        JSON.stringify(
                          option.value == null || option.value === ""
                            ? null
                            : String(option.value),
                        ),
                      ) &&
                      option.aliases?.some(
                        (alias) =>
                          String(alias).toLowerCase() ===
                          String(value).toLowerCase(),
                      ),
                  ) ?? false
                )
              }
              if (field.kind === "text") {
                return String(value ?? "")
                  .toLowerCase()
                  .includes((filter.text ?? "").trim().toLowerCase())
              }
              if (value == null || value === "") {
                return false
              }
              const numeric =
                field.kind === "date"
                  ? /^\d{4}-\d{2}-\d{2}$/.test(String(value))
                    ? dateBound(String(value), false)
                    : new Date(String(value)).getTime()
                  : Number(value)
              const min = filter.min
                ? field.kind === "date"
                  ? dateBound(filter.min, false)
                  : Number(filter.min)
                : -Infinity
              const max = filter.max
                ? field.kind === "date"
                  ? dateBound(filter.max, true)
                  : Number(filter.max)
                : Infinity
              return (
                Number.isFinite(numeric) && numeric >= min && numeric <= max
              )
            })
          }),
        )
  }, [rows, fields, values])
  const activeCount = fields.filter((field) =>
    isCollectionFilterActive(values[field.id] ?? {}),
  ).length
  const controls: CollectionFilterControls = {
    properties,
    values,
    onChange,
    reset,
    activeCount,
  }
  return { rows: filteredRows, controls }
}
